<template>
	<div class="related-post">
		<div
			v-if="isMobile"
			class="related-post-image"
		>
			<NuxtLink :to="url">
				<img
					height="640"
					width="640"
					:src="
						imageLinkFormatter(
							image,
							640,
							80,
							'PRODUCTION',
							'https://content.timothysykes.com'
						)
					"
					:alt="`Image for ${title} recomended post`"
					loading="lazy"
				/>
			</NuxtLink>
		</div>
		<div
			v-else
			class="related-post-image"
		>
			<img
				height="172"
				width="172"
				:src="
					imageLinkFormatter(
						image,
						172,
						80,
						'PRODUCTION',
						'https://content.timothysykes.com'
					)
				"
				:alt="`Image for ${title} recomended post`"
				loading="lazy"
			/>
		</div>

		<div class="related-post-info">
			<NuxtLink :to="url">
				<Heading
					tag="h6"
					:text="title"
				/>
			</NuxtLink>
			<div
				v-if="isNews"
				class="date-block"
			>
				<span class="flex gap-1 items-center">
					<svg
						class="transform scale-75 opacity-50"
						width="24"
						height="24"
						viewBox="0 0 24 24"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M12 2C6.477 2 2 6.477 2 12C2 17.523 6.477 22 12 22C17.523 22 22 17.523 22 12C22 6.477 17.523 2 12 2ZM15.293 16.707L11 12.414V6H13V11.586L16.707 15.293L15.293 16.707Z"
							fill="#475569"
						/>
					</svg>
					{{ text }} min read
				</span>
			</div>
			<div
				v-else
				v-html="contentFormatter(text)"
			/>
			<NuxtLink
				:to="url"
				class="read-more-link"
			>
				Read more
				<svg
					width="15"
					height="13"
					viewBox="0 0 15 13"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<line
						x1="12.0684"
						y1="6.5"
						x2="0.0683594"
						y2="6.5"
						stroke="#3b82f6"
						stroke-width="2"
					/>
					<path
						d="M8.40034 0.5L7.06836 1.91L11.3949 6.5L7.06836 11.09L8.40034 12.5L14.0684 6.5L8.40034 0.5Z"
						fill="#3b82f6"
					/>
				</svg>
			</NuxtLink>
		</div>
	</div>
</template>

<script setup lang="ts">
import { load } from "cheerio";

const isMobile = ref(false);

type RelatedPost = {
	image: string;
	title: string;
	text: string;
	url: string;
	isNews?: boolean;
};

defineProps<RelatedPost>();

const contentFormatter = (content: string) => {
	const $ = load(content, {
		decodeEntities: false, // Prevent HTML entity decoding
		xmlMode: false // Do not treat input as XML
	});

	$("p").each((_index, element) => {
		const $element = $(element);
		const text = $element.text();
		$element.text(`${text.slice(0, 124)}...`);
	});

	// Return the modified content
	return $.html();
};

onMounted(() => {
	if (process.client) {
		if (window.innerWidth <= 640) {
			isMobile.value = true;
		}
	}
});
</script>

<style lang="scss">
@import "@assets/utils.scss";

.related-post {
	display: flex;
	gap: 24px;
	flex-wrap: wrap;
	margin-bottom: 12px;
	@include breakpoint(md) {
		flex-wrap: nowrap;
		margin-bottom: 0;
	}
}

.related-post-image {
	display: flex;
	justify-content: flex-start;
	overflow: hidden;
	max-height: 220px;
	img {
		max-width: unset;
		width: auto;
		width: 100%;
	}

	@include breakpoint(sm) {
		min-height: 124px;
		max-height: 124px;
		min-width: 124px;
		max-width: 124px;

		img {
			height: 124px;
			max-width: unset;
			width: auto;
		}
	}
}

.end-of-article .related-post-info .read-more-link {
	font-size: 18px;
	margin-top: 20px;
	display: inline-flex;
	max-width: fit-content;
	align-items: center;
	position: relative;
	color: $primary-500;
	line-height: normal;
	&::after {
		content: "";
		background-image: url("/images/icons/external-link.svg");
		width: 14px;
		height: 14px;
		display: inline-block;
		background-size: contain;
		position: relative;
		left: -4px;
	}

	&:hover {
		background-color: #eab308;
		color: #1e3a8a;
		text-decoration: underline;
		&::after {
			background-image: url("/images/icons/external-link-hover.svg");
		}
	}
}
.related-post-info {
	display: flex;
	flex-direction: column;
	gap: 8px;
	color: $neutral-600;
}

.end-of-article .related-post-info h6 {
	color: $neutral-800;

	/* Heading/H2 */
	font-family: Fira Sans;
	font-size: 24px;
	font-style: normal;
	font-weight: 700;
	line-height: 120%;
	margin-bottom: 32px;

	/* 45.6px */
	letter-spacing: -0.76px;

	@include breakpoint(lg) {
		font-size: 32px;
	}
}
.end-of-article .related-post-info p a {
	font-size: 18px;
	display: revert-layer;
	align-items: center;
	position: relative;
	color: $primary-500;
	line-height: normal;
	&::after {
		content: "";
		background-image: url("/images/icons/external-link.svg");
		width: 14px;
		height: 14px;
		display: inline-block;
		background-size: contain;
		margin-left: 2px;
	}

	&:hover {
		background-color: #eab308;
		color: #1e3a8a;
		text-decoration: underline;
		&::after {
			background-image: url("/images/icons/external-link-hover.svg");
		}
	}
}

.related-post-info h6 {
	font-weight: 600;
	color: $neutral-900;

	@include breakpoint(lg) {
		font-size: 20px;
	}
}

.related-post-info p {
	font-family: Rubik;
	font-size: 18px;
	font-style: normal;
	font-weight: 400;
	line-height: 160%;
}

.read-more-link {
	color: $primary-500;
	font-family: Fira Sans;
	font-size: 16px;
	font-style: normal;
	font-weight: 600;
	display: flex;
	align-items: center;
	gap: 8px;
}
</style>
